import type {
  AgenticAgentSettingsCreate,
  CreateAgenticSettingsBody,
  CreateOnboardingBody,
  UpdateMerchantBody,
} from '../../../store/modules/merchant/types';
import {
  getBigCommerceExternalPlatformIdMismatchError,
  normalizeBigCommerceExternalId,
} from '../../../util/bigcommerceExternalId';
import { resolveVoiceProfileId } from '../agentCatalog';
import {
  getShopifyExternalPlatformIdMismatchError,
  normalizeShopifyExternalId,
} from '../../../util/shopifyExternalId';
import type { OnboardingDraft } from './OnboardingDraftContext';
import { BIGCOMMERCE_PLATFORM_ID, SHOPIFY_PLATFORM_ID, SQUARESPACE_PLATFORM_ID } from './platforms';
import {
  buildSquarespaceStoreLinkUpdate,
  getSquarespaceSiteError,
  type SquarespaceStoreLink,
} from './squarespaceStore';

export const SHOPIFY_ONBOARDING_CONFLICT_WARNING =
  'Shopify store onboarding was already started for this merchant. Skipify will continue from the existing checklist.';

export const SHOPIFY_ONBOARDING_FAILED_WARNING =
  'We could not register your Shopify store for provisioning. Your agent settings were saved — a Skipify team member can finish store setup.';

export const BIGCOMMERCE_ONBOARDING_CONFLICT_WARNING =
  'BigCommerce store onboarding was already started for this merchant. Skipify will continue from the existing checklist.';

export const BIGCOMMERCE_ONBOARDING_FAILED_WARNING =
  'We could not register your BigCommerce store for provisioning. Your agent settings were saved — please try again or contact support.';

export const SQUARESPACE_STORE_LINK_UNAVAILABLE_MESSAGE =
  'We could not load your store details. Please try again.';

export const SQUARESPACE_STORE_LINK_FAILED_MESSAGE =
  'We could not link your Squarespace site. Please try again.';

export const ELLA_AGENTIC_SETTINGS_FAILED_MESSAGE =
  'We could not save your agent configuration. Please try again.';

export type OnboardingPublishApiCall = {
  method: 'POST';
  path: string;
  description: string;
  body: Record<string, unknown>;
};

export type BuildOnboardingPublishPlanOptions = {
  merchantId: string;
  voiceProfileIds?: readonly string[];
};

const publishableRules = (draft: OnboardingDraft) =>
  (draft.rules ?? []).filter(
    (rule) => rule.enabled && rule.text.trim().length > 0 && (rule.name ?? '').trim().length > 0,
  );

export const buildShopifyOnboardingBody = (draft: OnboardingDraft): CreateOnboardingBody | null => {
  const shopifyExternalId =
    draft.store.platformId === SHOPIFY_PLATFORM_ID
      ? normalizeShopifyExternalId(draft.store.shopDomain)
      : null;

  if (!shopifyExternalId) {
    return null;
  }

  return {
    systemOfOrigin: 'shopify',
    externalPlatformId: shopifyExternalId,
  };
};

export const buildBigCommerceOnboardingBody = (
  draft: OnboardingDraft,
): CreateOnboardingBody | null => {
  const storeHash =
    draft.store.platformId === BIGCOMMERCE_PLATFORM_ID
      ? normalizeBigCommerceExternalId(draft.store.shopDomain)
      : null;

  if (!storeHash) {
    return null;
  }

  return {
    systemOfOrigin: 'bigcommerce',
    externalPlatformId: storeHash,
  };
};

export const buildPlatformOnboardingBody = (draft: OnboardingDraft): CreateOnboardingBody | null =>
  buildShopifyOnboardingBody(draft) ?? buildBigCommerceOnboardingBody(draft);

export const buildEllaAgenticSettingsCreatePayload = (
  draft: OnboardingDraft,
  { voiceProfileIds = [] }: Pick<BuildOnboardingPublishPlanOptions, 'voiceProfileIds'> = {},
): AgenticAgentSettingsCreate => {
  const { agentSetup } = draft;

  const payload: AgenticAgentSettingsCreate = {
    promptManagement: {
      formality: agentSetup.formality,
      sentenceStyle: agentSetup.sentenceStyle,
      customGreeting: agentSetup.customGreeting,
      brandVocabulary: {
        mirrorSiteTerminology: agentSetup.mirrorSiteTerminology,
      },
      suggestedPrompts: [...agentSetup.suggestedPrompts],
    },
    personalities: [
      {
        enabled: false,
        displayName: agentSetup.agentName.trim() || 'Ella',
        avatar: { src: agentSetup.avatarSrc },
        tone: agentSetup.tone,
        voice: {
          profileId: resolveVoiceProfileId(agentSetup.voiceProfileId, voiceProfileIds),
        },
      },
    ],
    voice: {
      pronunciationGlossary: [...(draft.voice?.pronunciationGlossary ?? [])],
    },
    productExperience: {
      productDisplay: agentSetup.productDisplay,
    },
    engagement: { ...draft.engagement },
    escalation: { ...draft.escalation },
    checkoutMode: { ...draft.checkoutMode },
    rules: publishableRules(draft),
  };

  return payload;
};

export const buildAgenticSettingsPostBody = (
  draft: OnboardingDraft,
  options: Pick<BuildOnboardingPublishPlanOptions, 'voiceProfileIds'> = {},
): CreateAgenticSettingsBody => ({
  agents: {
    Ella: buildEllaAgenticSettingsCreatePayload(draft, options),
  },
});

export const buildOnboardingPublishPlan = (
  draft: OnboardingDraft,
  { merchantId, voiceProfileIds = [] }: BuildOnboardingPublishPlanOptions,
): OnboardingPublishApiCall[] => {
  const calls: OnboardingPublishApiCall[] = [];
  const platformBody = buildPlatformOnboardingBody(draft);

  if (platformBody) {
    const description =
      platformBody.systemOfOrigin === 'shopify'
        ? 'Create Shopify platform onboarding checklist'
        : 'Create BigCommerce platform onboarding checklist';

    calls.push({
      method: 'POST',
      path: `/merchants/${merchantId}/onboardings`,
      description,
      body: platformBody,
    });
  }

  calls.push({
    method: 'POST',
    path: `/merchants/${merchantId}/agentic-settings`,
    description: 'Create Agent Hub agentic settings',
    body: buildAgenticSettingsPostBody(draft, { voiceProfileIds }),
  });

  return calls;
};

export type OnboardingPublishSuccess = {
  status: 'success';
  shopifyWarning?: string;
  shopifyQueued?: boolean;
  bigcommerceWarning?: string;
  bigcommerceQueued?: boolean;
};

export type OnboardingPublishFailure = {
  status: 'error';
  message: string;
};

export type OnboardingPublishResult = OnboardingPublishSuccess | OnboardingPublishFailure;

type UnwrapMutation<T> = () => Promise<T>;

export type ExecuteOnboardingPublishArgs = BuildOnboardingPublishPlanOptions & {
  draft: OnboardingDraft;
  merchantExternalPlatformId?: string | null;
  /** Required for Squarespace; undefined means it failed to load. */
  squarespaceStoreLink?: SquarespaceStoreLink;
  updateMerchant?: (body: UpdateMerchantBody) => { unwrap: UnwrapMutation<unknown> };
  createOnboarding?: (body: CreateOnboardingBody) => { unwrap: UnwrapMutation<unknown> };
  postAgenticSettings: (body: CreateAgenticSettingsBody) => { unwrap: UnwrapMutation<unknown> };
};

const isConflict = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && 'status' in error && error.status === 409);

const isBadRequest = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && 'status' in error && error.status === 400);

export const getOnboardingPublishErrorMessage = (error: unknown, fallback: string): string => {
  const errors = (error as { data?: { errors?: string[] } })?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(' ');
  }

  const message = (error as { data?: { message?: string } })?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallback;
};

const getPlatformMismatchError = (
  body: CreateOnboardingBody,
  merchantExternalPlatformId?: string | null,
): string | undefined => {
  if (body.systemOfOrigin === 'shopify') {
    return getShopifyExternalPlatformIdMismatchError(
      body.externalPlatformId,
      merchantExternalPlatformId,
    );
  }

  return getBigCommerceExternalPlatformIdMismatchError(
    body.externalPlatformId,
    merchantExternalPlatformId,
  );
};

export const executeOnboardingPublish = async ({
  draft,
  merchantExternalPlatformId,
  squarespaceStoreLink,
  voiceProfileIds = [],
  updateMerchant,
  createOnboarding,
  postAgenticSettings,
}: ExecuteOnboardingPublishArgs): Promise<OnboardingPublishResult> => {
  // Linked before saving the agent; retrying is safe since an already-linked site is skipped.
  if (draft.store.platformId === SQUARESPACE_PLATFORM_ID) {
    if (!squarespaceStoreLink) {
      return { status: 'error', message: SQUARESPACE_STORE_LINK_UNAVAILABLE_MESSAGE };
    }

    const siteError = getSquarespaceSiteError(draft.store.shopDomain, squarespaceStoreLink);
    if (siteError) {
      return { status: 'error', message: siteError };
    }

    const ecommerceConnector = buildSquarespaceStoreLinkUpdate(
      draft.store.shopDomain,
      squarespaceStoreLink,
    );
    if (ecommerceConnector && updateMerchant) {
      try {
        await updateMerchant({ ecommerceConnector }).unwrap();
      } catch (error) {
        return {
          status: 'error',
          message: getOnboardingPublishErrorMessage(error, SQUARESPACE_STORE_LINK_FAILED_MESSAGE),
        };
      }
    }
  }

  const platformBody = buildPlatformOnboardingBody(draft);
  let shopifyWarning: string | undefined;
  let shopifyQueued = false;
  let bigcommerceWarning: string | undefined;
  let bigcommerceQueued = false;

  if (platformBody && createOnboarding) {
    const mismatchError = getPlatformMismatchError(platformBody, merchantExternalPlatformId);
    if (mismatchError) {
      return {
        status: 'error',
        message: mismatchError,
      };
    }

    const failedWarning =
      platformBody.systemOfOrigin === 'shopify'
        ? SHOPIFY_ONBOARDING_FAILED_WARNING
        : BIGCOMMERCE_ONBOARDING_FAILED_WARNING;
    const conflictWarning =
      platformBody.systemOfOrigin === 'shopify'
        ? SHOPIFY_ONBOARDING_CONFLICT_WARNING
        : BIGCOMMERCE_ONBOARDING_CONFLICT_WARNING;

    try {
      await createOnboarding(platformBody).unwrap();
      if (platformBody.systemOfOrigin === 'shopify') {
        shopifyQueued = true;
      } else {
        bigcommerceQueued = true;
      }
    } catch (error) {
      if (isConflict(error)) {
        if (platformBody.systemOfOrigin === 'shopify') {
          shopifyWarning = conflictWarning;
        } else {
          bigcommerceWarning = conflictWarning;
        }
      } else if (isBadRequest(error)) {
        return {
          status: 'error',
          message: getOnboardingPublishErrorMessage(error, failedWarning),
        };
      } else if (platformBody.systemOfOrigin === 'shopify') {
        shopifyWarning = failedWarning;
      } else {
        bigcommerceWarning = failedWarning;
      }
    }
  }

  try {
    await postAgenticSettings(buildAgenticSettingsPostBody(draft, { voiceProfileIds })).unwrap();
  } catch (error) {
    return {
      status: 'error',
      message: getOnboardingPublishErrorMessage(error, ELLA_AGENTIC_SETTINGS_FAILED_MESSAGE),
    };
  }

  return {
    status: 'success',
    shopifyWarning,
    ...(shopifyQueued ? { shopifyQueued: true } : {}),
    bigcommerceWarning,
    ...(bigcommerceQueued ? { bigcommerceQueued: true } : {}),
  };
};
